import { PhoneOff, FileQuestion, Database, UserMinus, Star, EyeOff } from "lucide-react";
import { Eyebrow, Reveal } from "./utils";

const leaks = [
  { icon: PhoneOff, title: "Missed Calls and Slow Response" },
  { icon: FileQuestion, title: "Quotes and Estimates That Go Quiet" },
  { icon: Database, title: "Disorganized Leads and Customer Data" },
  { icon: UserMinus, title: "Past Customers Never Re-Engaged" },
  { icon: Star, title: "Reviews and Referrals Left to Chance" },
  { icon: EyeOff, title: "No Clear View of What Is Working" },
];

function LeakCard({ icon: Icon, title, testid }) {
  return (
    <div
      data-testid={testid}
      className="group flex items-center gap-4 rounded-2xl glass-panel p-5 transition-all duration-300 hover:-translate-y-1 hover:border-brand-gold/50 hover:shadow-luxe-gold"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-gold/10 transition-colors group-hover:bg-brand-gold/18">
        <Icon className="h-5 w-5 text-brand-gold" strokeWidth={1.5} />
      </span>
      <p className="font-sans text-sm md:text-[15px] font-medium leading-snug text-brand-black">{title}</p>
    </div>
  );
}

export default function RevenueLeakMap() {
  return (
    <section data-testid="revenue-leak-map" id="leak-map" className="relative overflow-hidden bg-brand-gray-light py-24 md:py-32">
      <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full bg-brand-gold/8 blur-[130px]" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <Reveal><Eyebrow>Where Opportunities Commonly Get Lost</Eyebrow></Reveal>
          <Reveal delay={1}>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tight text-brand-black leading-[1.08]">
              The Problem Is Usually Not Just <span className="gold-text">“More Leads.”</span>
            </h2>
          </Reveal>
        </div>

        {/* Hub & spoke */}
        <div className="relative mt-16 grid lg:grid-cols-[1fr_auto_1fr] gap-8 lg:gap-12 items-center">
          {/* Left column */}
          <div className="space-y-6 order-2 lg:order-1">
            {leaks.slice(0, 3).map((l, i) => (
              <Reveal key={l.title} delay={i}>
                <LeakCard {...l} testid={`leak-card-${i}`} />
              </Reveal>
            ))}
          </div>

          {/* Central hub */}
          <Reveal className="order-1 lg:order-2 flex justify-center">
            <div className="relative flex h-[260px] w-[260px] md:h-[300px] md:w-[300px] items-center justify-center rounded-full bg-brand-black gold-glow border border-brand-gold/25">
              <div className="pointer-events-none absolute inset-3 rounded-full border border-brand-gold/15" />
              <div className="pointer-events-none absolute inset-8 rounded-full border border-brand-gold/10" />
              <div className="px-8 text-center">
                <p className="font-serif text-xl md:text-2xl tracking-tight text-white leading-tight">
                  THE <span className="gold-text">REVENUE</span><br />LEAK MAP
                </p>
                <p className="mt-3 font-sans text-[11px] leading-relaxed text-white/55">
                  Find the gaps. Prioritize the opportunity. Build the right system.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Right column */}
          <div className="space-y-6 order-3">
            {leaks.slice(3, 6).map((l, i) => (
              <Reveal key={l.title} delay={i}>
                <LeakCard {...l} testid={`leak-card-${i + 3}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
